export const PLAN_NAMES = Object.freeze({
  FREE: 'Free',
  PREMIUM: 'Premium',
  SPECIALIST: 'Especialista'
});

export const EFFECTIVE_PLAN_REASONS = Object.freeze({
  ANONYMOUS: 'anonymous',
  FREE: 'free',
  BLOCKED: 'blocked',
  EXPIRED: 'expired',
  ADMIN: 'admin',
  SPECIALIST: 'specialist',
  ACTIVE_WITH_EXPIRATION: 'active_with_expiration',
  ACTIVE_WITHOUT_EXPIRATION: 'active_without_expiration'
});

const PRIVILEGED_ROLES = new Set(['admin', 'especialista']);

const normalizeValue = (value) => String(value || '').trim();

export const normalizeRole = (role) => normalizeValue(role).toLowerCase();

export const normalizePlanName = (plan) => {
  const normalized = normalizeValue(plan);
  const normalizedKey = normalized.toLowerCase();

  if (!normalized) return PLAN_NAMES.FREE;
  if (normalizedKey === 'free' || normalizedKey === 'gratuito') return PLAN_NAMES.FREE;
  if (normalizedKey === 'premium') return PLAN_NAMES.PREMIUM;
  if (normalizedKey === 'especialista') return PLAN_NAMES.SPECIALIST;

  return normalized;
};

export const isAdminOrSpecialist = (user) => {
  const role = normalizeRole(user?.role);
  return PRIVILEGED_ROLES.has(role);
};

export const isBlockedUser = (user) => (
  user?.blocked === true ||
  user?.active === false
);

export const parsePlanExpirationDate = (value) => {
  if (!value) return null;

  if (value instanceof Date) {
    return Number.isNaN(value.getTime()) ? null : value;
  }

  if (typeof value?.toDate === 'function') {
    const parsedDate = value.toDate();
    return parsedDate instanceof Date && !Number.isNaN(parsedDate.getTime())
      ? parsedDate
      : null;
  }

  if (typeof value?.seconds === 'number') {
    const milliseconds = value.seconds * 1000 + Math.floor((value.nanoseconds || 0) / 1000000);
    const parsedDate = new Date(milliseconds);
    return Number.isNaN(parsedDate.getTime()) ? null : parsedDate;
  }

  if (typeof value === 'string' || typeof value === 'number') {
    const parsedDate = new Date(value);
    return Number.isNaN(parsedDate.getTime()) ? null : parsedDate;
  }

  return null;
};

export const isPlanExpired = (user, options = {}) => {
  if (isAdminOrSpecialist(user)) return false;

  const now = options.now instanceof Date ? options.now : new Date();
  const expirationDate = parsePlanExpirationDate(user?.planExpirationDate);

  return !!expirationDate && expirationDate.getTime() <= now.getTime();
};

export function getEffectivePlan(user, options = {}) {
  const originalPlano = normalizePlanName(user?.plano || user?.plan);
  const expirationDate = parsePlanExpirationDate(user?.planExpirationDate);
  const now = options.now instanceof Date ? options.now : new Date();
  const isBlocked = isBlockedUser(user);
  const role = normalizeRole(user?.role);

  if (!user) {
    return {
      plano: PLAN_NAMES.FREE,
      originalPlano: PLAN_NAMES.FREE,
      hasPlanAccess: false,
      isPremium: false,
      shouldShowPremiumOffer: true,
      isAdminOrSpecialist: false,
      isBlocked: false,
      isExpired: false,
      expirationDate: null,
      reason: EFFECTIVE_PLAN_REASONS.ANONYMOUS
    };
  }

  if (isBlocked) {
    return {
      plano: PLAN_NAMES.FREE,
      originalPlano,
      hasPlanAccess: false,
      isPremium: false,
      shouldShowPremiumOffer: false,
      isAdminOrSpecialist: PRIVILEGED_ROLES.has(role),
      isBlocked: true,
      isExpired: false,
      expirationDate,
      reason: EFFECTIVE_PLAN_REASONS.BLOCKED
    };
  }

  if (role === 'admin') {
    return {
      plano: PLAN_NAMES.PREMIUM,
      originalPlano,
      hasPlanAccess: true,
      isPremium: true,
      shouldShowPremiumOffer: false,
      isAdminOrSpecialist: true,
      isBlocked: false,
      isExpired: false,
      expirationDate,
      reason: EFFECTIVE_PLAN_REASONS.ADMIN
    };
  }

  if (role === 'especialista') {
    return {
      plano: PLAN_NAMES.SPECIALIST,
      originalPlano,
      hasPlanAccess: true,
      isPremium: true,
      shouldShowPremiumOffer: false,
      isAdminOrSpecialist: true,
      isBlocked: false,
      isExpired: false,
      expirationDate,
      reason: EFFECTIVE_PLAN_REASONS.SPECIALIST
    };
  }

  const hasPaidPlan = originalPlano !== PLAN_NAMES.FREE;
  const isExpired = !!expirationDate && expirationDate.getTime() <= now.getTime();

  if (hasPaidPlan && isExpired) {
    return {
      plano: PLAN_NAMES.FREE,
      originalPlano,
      hasPlanAccess: false,
      isPremium: false,
      shouldShowPremiumOffer: true,
      isAdminOrSpecialist: false,
      isBlocked: false,
      isExpired: true,
      expirationDate,
      reason: EFFECTIVE_PLAN_REASONS.EXPIRED
    };
  }

  return {
    plano: originalPlano,
    originalPlano,
    hasPlanAccess: hasPaidPlan,
    isPremium: hasPaidPlan,
    shouldShowPremiumOffer: !hasPaidPlan,
    isAdminOrSpecialist: false,
    isBlocked: false,
    isExpired: false,
    expirationDate,
    reason: hasPaidPlan && expirationDate
      ? EFFECTIVE_PLAN_REASONS.ACTIVE_WITH_EXPIRATION
      : hasPaidPlan
        ? EFFECTIVE_PLAN_REASONS.ACTIVE_WITHOUT_EXPIRATION
        : EFFECTIVE_PLAN_REASONS.FREE
  };
}

export const getEffectivePlano = (user, options = {}) => getEffectivePlan(user, options).plano;

export const hasActivePlanAccess = (user, options = {}) => (
  getEffectivePlan(user, options).hasPlanAccess
);

export const shouldShowPremiumOffer = (user, options = {}) => (
  getEffectivePlan(user, options).shouldShowPremiumOffer
);
